import { useApolloClient, gql, TypedDocumentNode } from '@apollo/client'
import { cartDetailsFragment, cartFragment } from '@emico-hooks/cart-fragment'
import { useAuthorizationContext } from '@emico-hooks/login-token'
import { useCallback } from 'react'

import {
  SetGuestEmailOnCartMutation,
  SetGuestEmailOnCartMutationVariables,
} from './useSetGuestEmailOnCart.generated'

const setGuestEmailOnCartMutation = gql`
  mutation setGuestEmailOnCart($cartId: String!, $email: String!) {
    setGuestEmailOnCart(input: { cart_id: $cartId, email: $email }) {
      cart {
        ...CartFragment
        ...CartDetailsFragment
      }
    }
  }

  ${cartFragment}
  ${cartDetailsFragment}
` as TypedDocumentNode<
  SetGuestEmailOnCartMutation,
  SetGuestEmailOnCartMutationVariables
>

/**
 * Mutation to set a guest email address on the cart
 *
 * Usage example:
 *
 * ```ts
 * export function exampleFunction() {
 *  const cartId = useCartId()
 *  const setGuestEmailOnCart = useSetGuestEmailOnCart()
 *
 *  return useCallback(
 *    async (values: ExampleFormValues) => {
 *      try {
 *        await setGuestEmailOnCart(cartId, values.email)
 *      } catch(error) {
 *        // error
 *      }
 *    },
 *    [cartId, setGuestEmailOnCart],
 *  )
 * }
 * ```
 */

export function useSetGuestEmailOnCart() {
  const client = useApolloClient()
  const authorizationContext = useAuthorizationContext()

  return useCallback(
    /**
     *
     * @param cartId Cart id where the email address should be added to
     * @param email Email address that should be added to the cart
     */
    (cartId: string, email: string) =>
      client.mutate({
        mutation: setGuestEmailOnCartMutation,
        variables: {
          cartId,
          email,
        },
        context: authorizationContext,
      }),

    [authorizationContext, client],
  )
}
